import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import { UserFormServiceButtonClose, UserFormServiceButtonCreated } from "../MyBooking/styled/DesStyled";
import DialogWrap from "../../controls/components/dialogWrap/DialogWrap";
import { AdminContentBlogManagement } from "../AdminContentManage/AdminContentDialog";
import sanitizeHtml from "../../../utils/sanitizeHtml";
import {
	StationDocument,
	StationDocumentVersionRecord,
	DocumentType,
	DocumentTarget,
	getStationDocuments,
	getDocumentContent,
	getDocumentVersions,
	saveDocumentContent,
	toggleStationDocument,
	StationSigningSwitch,
	getStationSigningSwitch,
	setStationSigningSwitch,
	setStationResignSwitch,
} from "../../../services/StationDocumentService";
import PermissionGate from "../../controls/auth/PermissionGate";

/* ─── Layout ──────────────────────────────────────────────────── */

const Shell = styled(MyBookingWrapper)`
	padding: 20px 0 48px;
`;

const Inner = styled.div`
	width: 1200px;
	max-width: 100%;
	margin: 0 auto;

	@media (max-width: 1282px) {
		width: 100%;
		padding: 0 16px;
	}
`;

const PageHeader = styled.div`
	margin-bottom: 28px;
`;

const PageTitle = styled.h1`
	margin: 0;
	font-size: 22px;
	font-weight: 700;
	color: #111827;
`;

const PageMeta = styled.div`
	margin-top: 4px;
	font-size: 13px;
	color: #6b7280;
`;

/* ─── Section ─────────────────────────────────────────────────── */

const Section = styled.div`
	& + & {
		margin-top: 32px;
	}
`;

const SectionHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 14px;
`;

const SectionTitle = styled.div`
	font-size: 13px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.07em;
	color: #6b7280;
`;

const SectionLine = styled.div`
	flex: 1;
	height: 1px;
	background: #e5e7eb;
`;

const SectionCount = styled.span`
	font-size: 11px;
	font-weight: 700;
	color: #9ca3af;
`;

/* ─── Grid ────────────────────────────────────────────────────── */

const Grid = styled.div<{ $cols: number }>`
	display: grid;
	grid-template-columns: repeat(${({ $cols }) => $cols}, 1fr);
	gap: 14px;

	@media (max-width: 1024px) {
		grid-template-columns: repeat(2, 1fr);
	}
	@media (max-width: 640px) {
		grid-template-columns: 1fr;
	}
`;

/* ─── Card ────────────────────────────────────────────────────── */

const Card = styled.div`
	background: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 16px;
	padding: 18px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
`;

const CardTop = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
`;

const FileIcon = styled.div`
	width: 40px;
	height: 40px;
	border-radius: 10px;
	background: #fef2f2;
	color: #ef4444;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 16px;
	flex-shrink: 0;
`;

const CardInfo = styled.div`
	flex: 1;
	min-width: 0;
`;

const CardName = styled.div`
	font-size: 15px;
	font-weight: 700;
	color: #111827;
	line-height: 1.35;
`;

const BadgeRow = styled.div`
	display: flex;
	gap: 6px;
	flex-wrap: wrap;
	margin-top: 6px;
`;

const Badge = styled.span<{ $color: string; $bg: string }>`
	padding: 2px 8px;
	border-radius: 999px;
	font-size: 10px;
	font-weight: 700;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	color: ${({ $color }) => $color};
	background: ${({ $bg }) => $bg};
`;

const ContentStatus = styled.div<{ $hasContent: boolean }>`
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 12px;
	color: ${({ $hasContent }) => ($hasContent ? "#059669" : "#9ca3af")};
	font-weight: 500;
`;

const ContentLabelsWrap = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const ContentLabelsTitle = styled.div`
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: #94a3b8;
`;

const ContentLabelsList = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
`;

const ContentLabelChip = styled.span`
	display: inline-flex;
	align-items: center;
	max-width: 100%;
	padding: 5px 10px;
	border-radius: 999px;
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	color: #334155;
	font-size: 12px;
	font-weight: 600;
	line-height: 1.4;
`;

const CardDivider = styled.div`
	height: 1px;
	background: #f1f5f9;
`;

const CardFooter = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	flex-wrap: wrap;
`;

const ActionGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
	justify-content: flex-end;
`;

const ToggleWrap = styled.label`
	display: flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
`;

const ToggleInput = styled.input`
	display: none;
`;

const ToggleTrack = styled.div<{ $on: boolean }>`
	width: 36px;
	height: 20px;
	border-radius: 999px;
	background: ${({ $on }) => ($on ? "#2563eb" : "#d1d5db")};
	position: relative;
	transition: background 0.2s;
	flex-shrink: 0;

	&::after {
		content: "";
		position: absolute;
		top: 2px;
		left: ${({ $on }) => ($on ? "18px" : "2px")};
		width: 16px;
		height: 16px;
		border-radius: 50%;
		background: #ffffff;
		transition: left 0.2s;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
	}
`;

const ToggleLabel = styled.span<{ $on: boolean }>`
	font-size: 12px;
	font-weight: 600;
	color: ${({ $on }) => ($on ? "#2563eb" : "#9ca3af")};
`;

const EditBtn = styled.button`
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 7px 14px;
	border-radius: 10px;
	border: 1px solid #2563eb;
	background: #eff6ff;
	color: #2563eb;
	font-size: 13px;
	font-weight: 700;
	cursor: pointer;
	transition: background 0.15s;
	white-space: nowrap;

	&:hover {
		background: #dbeafe;
	}
`;

const HistoryBtn = styled(EditBtn)`
	border-color: #d1d5db;
	background: #ffffff;
	color: #374151;

	&:hover {
		background: #f8fafc;
	}
`;

/* ─── Editor Dialog ───────────────────────────────────────────── */

const DialogDocHeader = styled.div`
	padding: 16px 24px 0;
`;

const DialogDocName = styled.div`
	font-size: 16px;
	font-weight: 700;
	color: #111827;
`;

const DialogDocBadges = styled.div`
	display: flex;
	gap: 6px;
	margin-top: 6px;
`;

const DialogBody = styled.div`
	padding: 12px 24px 24px;
`;

const EditorControls = styled.div`
	display: flex;
	flex-direction: column;
	gap: 14px;
	padding: 12px 24px 0;
`;

const EditorControlRow = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 220px) minmax(0, 1fr);
	gap: 16px;

	@media (max-width: 768px) {
		grid-template-columns: 1fr;
	}
`;

const EditorField = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

const EditorLabel = styled.label`
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	color: #6b7280;
`;

const EditorInput = styled.input`
	width: 100%;
	border: 1px solid #d1d5db;
	border-radius: 10px;
	padding: 10px 12px;
	font-size: 14px;
	color: #111827;
	outline: none;

	&:focus {
		border-color: #2563eb;
		box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
	}
`;

const EditorHint = styled.div`
	font-size: 12px;
	line-height: 1.5;
	color: #6b7280;
`;

const PublishToggleWrap = styled.label`
	display: flex;
	align-items: flex-start;
	gap: 10px;
	padding: 12px 14px;
	border: 1px solid #dbe4ee;
	border-radius: 12px;
	background: #f8fbff;
	cursor: pointer;
`;

const PublishCheckbox = styled.input`
	margin-top: 3px;
`;

const PublishToggleCopy = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const PublishToggleTitle = styled.div`
	font-size: 14px;
	font-weight: 700;
	color: #111827;
`;

const PublishToggleMeta = styled.div`
	font-size: 12px;
	line-height: 1.5;
	color: #6b7280;
`;

const HistoryLayout = styled.div`
	display: grid;
	grid-template-columns: 280px minmax(0, 1fr);
	gap: 16px;
	align-items: start;

	@media (max-width: 1024px) {
		grid-template-columns: 1fr;
	}
`;

const HistorySidebar = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

const HistorySidebarTitle = styled.div`
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: #6b7280;
`;

const HistoryList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	max-height: 460px;
	overflow: auto;
	padding-right: 4px;
`;

const HistoryItem = styled.button<{ $active: boolean }>`
	width: 100%;
	border: 1px solid ${({ $active }) => ($active ? "#2563eb" : "#e5e7eb")};
	background: ${({ $active }) => ($active ? "#eff6ff" : "#ffffff")};
	border-radius: 12px;
	padding: 12px;
	text-align: left;
	cursor: pointer;
	transition: border-color 0.15s, background 0.15s;

	&:hover {
		border-color: #93c5fd;
		background: ${({ $active }) => ($active ? "#dbeafe" : "#f8fafc")};
	}
`;

const HistoryItemTop = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
`;

const HistoryVersion = styled.div`
	font-size: 14px;
	font-weight: 700;
	color: #111827;
`;

const HistoryMeta = styled.div`
	margin-top: 6px;
	font-size: 12px;
	line-height: 1.5;
	color: #6b7280;
`;

const HistoryEmpty = styled.div`
	border: 1px dashed #d1d5db;
	border-radius: 12px;
	padding: 16px;
	font-size: 13px;
	color: #6b7280;
	background: #f9fafb;
`;

const CompareSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	min-width: 0;
`;

const CompareHeader = styled.div`
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: #6b7280;
`;

const CompareGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 16px;

	@media (max-width: 1024px) {
		grid-template-columns: 1fr;
	}
`;

const ComparePane = styled.div`
	border: 1px solid #e5e7eb;
	border-radius: 14px;
	background: #ffffff;
	min-width: 0;
	overflow: hidden;
`;

const ComparePaneHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 14px 16px;
	background: #f8fafc;
	border-bottom: 1px solid #e5e7eb;
`;

const ComparePaneTitle = styled.div`
	font-size: 14px;
	font-weight: 700;
	color: #111827;
`;

const ComparePaneMeta = styled.div`
	font-size: 12px;
	color: #6b7280;
`;

const ComparePaneBody = styled.div`
	padding: 16px;
	min-height: 320px;
	max-height: 520px;
	overflow: auto;
	color: #374151;

	h1,
	h2,
	h3,
	h4,
	h5,
	h6 {
		color: #111827;
	}

	p,
	li {
		line-height: 1.6;
	}
`;

const CompareEmpty = styled.div`
	font-size: 13px;
	color: #9ca3af;
`;

const LoadingState = styled.div`
	padding: 28px 0;
	text-align: center;
	font-size: 14px;
	color: #6b7280;
`;

function EditorDialog(props: {
	open: boolean;
	doc: StationDocument | null;
	onClose: () => void;
	onSave: (payload: {
		name: string;
		content: string;
		versionLabel: string;
		publishToStations: boolean;
	}) => Promise<void>;
}) {
	const editorRef = useRef<any>(null);
	const [documentName, setDocumentName] = useState("");
	const [content, setContent] = useState("");
	const [saving, setSaving] = useState(false);
	const [versionLabel, setVersionLabel] = useState("");
	const [publishToStations, setPublishToStations] = useState(false);
	const [latestVersion, setLatestVersion] = useState<StationDocumentVersionRecord | null>(null);

	useEffect(() => {
		if (props.open && props.doc) {
			Promise.all([getDocumentContent(props.doc.id), getDocumentVersions(props.doc.id)]).then(
				([loadedContent, loadedVersions]) => {
					setDocumentName(props.doc?.name || "");
					setContent(loadedContent);
					setLatestVersion(loadedVersions[0] || null);
					setVersionLabel("");
					setPublishToStations(false);
				}
			);
			return;
		}

		setDocumentName("");
		setContent("");
		setLatestVersion(null);
		setVersionLabel("");
		setPublishToStations(false);
	}, [props.open, props.doc?.id]);

	const handleSave = async () => {
		const html = editorRef.current?.getContent?.() || "";
		setSaving(true);
		try {
			await props.onSave({
				name: documentName,
				content: html,
				versionLabel,
				publishToStations,
			});
			props.onClose();
		} finally {
			setSaving(false);
		}
	};

	if (!props.doc) return null;
	const typeBadge = getTypeBadge(props.doc.type);
	const targetBadge = getTargetBadge(props.doc.target);

	return (
		<DialogWrap
			open={props.open}
			onClose={() => props.onClose()}
			disableAutoFocus
			disableRestoreFocus
			disableEnforceFocus
			title={documentName || props.doc.name}
			actions={
				<>
					<UserFormServiceButtonClose onClick={props.onClose}>Cancel</UserFormServiceButtonClose>
					<PermissionGate permission="documents.manage">
						<UserFormServiceButtonCreated
							onClick={handleSave}
							disabled={saving}>
							{saving ? "Saving..." : "Save"}
						</UserFormServiceButtonCreated>
					</PermissionGate>
				</>
			}
			login={true}>
			<DialogDocHeader>
				<DialogDocBadges>
					<Badge
						$color={typeBadge.color}
						$bg={typeBadge.bg}>
						{typeBadge.label}
					</Badge>
					<Badge
						$color={targetBadge.color}
						$bg={targetBadge.bg}>
						{targetBadge.label}
					</Badge>
				</DialogDocBadges>
			</DialogDocHeader>
			<EditorControls>
				<EditorControlRow>
					<EditorField>
						<EditorLabel htmlFor="station-document-name">Document name</EditorLabel>
						<EditorInput
							id="station-document-name"
							value={documentName}
							onChange={(event) => setDocumentName(event.target.value)}
							placeholder="e.g. Business Cooperation Agreement"
						/>
						<EditorHint>
							This name is shown in admin history and in the station portal for the live document.
						</EditorHint>
					</EditorField>
				</EditorControlRow>
				<EditorControlRow>
					<EditorField>
						<EditorLabel htmlFor="station-document-version-label">Version</EditorLabel>
						<EditorInput
							id="station-document-version-label"
							value={versionLabel}
							onChange={(event) => setVersionLabel(event.target.value)}
							placeholder="e.g. 1.2.1"
						/>
						<EditorHint>
							Latest saved version: {getVersionDisplayLabel(latestVersion)}. Use the legal version label
							you want to keep in history and consent logs.
						</EditorHint>
					</EditorField>

					<EditorField>
						<EditorLabel>Publish</EditorLabel>
						<PublishToggleWrap>
							<PublishCheckbox
								type="checkbox"
								checked={publishToStations}
								onChange={(event) => setPublishToStations(event.target.checked)}
							/>
							<PublishToggleCopy>
								<PublishToggleTitle>
									{props.doc.type === "agreement"
										? "Publish to stations and require re-agreement"
										: "Publish this version to stations"}
								</PublishToggleTitle>
								<PublishToggleMeta>
									{props.doc.type === "agreement"
										? "When enabled, this version becomes the live legal version shown to stations and they must accept it before continuing."
										: "When enabled, this version becomes the live document shown to stations."}
								</PublishToggleMeta>
							</PublishToggleCopy>
						</PublishToggleWrap>
						<EditorHint>
							Leave this off for internal or minor edits. The content will be saved in history as a draft
							only.
						</EditorHint>
					</EditorField>
				</EditorControlRow>
			</EditorControls>
			<AdminContentBlogManagement
				dialogType={`station_document_${props.doc.id}`}
				content={content}
				setContent={setContent}
				editorRef={editorRef}
			/>
		</DialogWrap>
	);
}

/* ─── Helpers ─────────────────────────────────────────────────── */

function getTypeBadge(type: DocumentType) {
	return type === "agreement"
		? { label: "Agreement", color: "#92400e", bg: "#fef3c7" }
		: { label: "View only", color: "#374151", bg: "#f3f4f6" };
}

function getTargetBadge(target: DocumentTarget) {
	return target === "exclusive_only"
		? { label: "Exclusive only", color: "#7c3aed", bg: "#f5f3ff" }
		: { label: "All stations", color: "#0f766e", bg: "#ecfdf5" };
}

function getVersionDisplayLabel(version?: Pick<StationDocumentVersionRecord, "version" | "versionLabel"> | null) {
	if (typeof version?.versionLabel === "string" && version.versionLabel.trim()) {
		return version.versionLabel.trim();
	}

	if (version?.version !== undefined && version?.version !== null) {
		return String(version.version);
	}

	return "-";
}

function formatDate(iso?: string) {
	if (!iso) return "";
	try {
		return new Date(iso).toLocaleDateString("en-GB");
	} catch {
		return iso;
	}
}

function formatDateTime(iso?: string | null) {
	if (!iso) return "-";
	try {
		return new Date(iso).toLocaleString("en-GB");
	} catch {
		return iso;
	}
}

function decodeHtmlEntities(value: string) {
	if (!value) return "";

	if (typeof document !== "undefined") {
		const textarea = document.createElement("textarea");
		textarea.innerHTML = value;
		return textarea.value;
	}

	return value
		.replace(/&nbsp;/gi, " ")
		.replace(/&amp;/gi, "&")
		.replace(/&lt;/gi, "<")
		.replace(/&gt;/gi, ">")
		.replace(/&quot;/gi, '"')
		.replace(/&#39;/gi, "'");
}

function normalizeContentLabel(value: string) {
	return decodeHtmlEntities(value)
		.replace(/<[^>]+>/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

function extractContentLabels(content?: string) {
	if (!content) return [];

	const labels: string[] = [];
	const seen = new Set<string>();

	const pushLabel = (value: string) => {
		const normalized = normalizeContentLabel(value);
		if (!normalized) return;
		const key = normalized.toLowerCase();
		if (seen.has(key)) return;
		seen.add(key);
		labels.push(normalized);
	};

	const headingRegex = /<h[1-6][^>]*>([\s\S]*?)<\/h[1-6]>/gi;
	let headingMatch: RegExpExecArray | null = null;
	while ((headingMatch = headingRegex.exec(content)) !== null) {
		pushLabel(headingMatch[1]);
		if (labels.length >= 4) return labels;
	}

	const blockRegex = /<(p|li|strong|b)[^>]*>([\s\S]*?)<\/\1>/gi;
	let blockMatch: RegExpExecArray | null = null;
	while ((blockMatch = blockRegex.exec(content)) !== null) {
		pushLabel(blockMatch[2]);
		if (labels.length >= 4) break;
	}

	return labels;
}

function getDefaultHistoryVersion(versions: StationDocumentVersionRecord[]) {
	if (versions.length > 1) {
		return versions[1];
	}
	return versions[0] || null;
}

function HistoryDialog(props: { open: boolean; doc: StationDocument | null; onClose: () => void }) {
	const [currentContent, setCurrentContent] = useState("");
	const [versions, setVersions] = useState<StationDocumentVersionRecord[]>([]);
	const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		let mounted = true;

		if (!props.open || !props.doc) {
			setCurrentContent("");
			setVersions([]);
			setSelectedVersionId(null);
			setLoading(false);
			return;
		}

		setLoading(true);
		Promise.all([getDocumentContent(props.doc.id), getDocumentVersions(props.doc.id)])
			.then(([nextContent, nextVersions]) => {
				if (!mounted) return;
				setCurrentContent(nextContent || "");
				setVersions(nextVersions);
				setSelectedVersionId(getDefaultHistoryVersion(nextVersions)?.id || null);
			})
			.catch((error: any) => {
				if (!mounted) return;
				window.alert(error?.response?.data?.message || error?.message || "Failed to load document history.");
				setCurrentContent("");
				setVersions([]);
				setSelectedVersionId(null);
			})
			.finally(() => {
				if (mounted) {
					setLoading(false);
				}
			});

		return () => {
			mounted = false;
		};
	}, [props.open, props.doc]);

	if (!props.doc) return null;

	const selectedVersion =
		versions.find((version) => version.id === selectedVersionId) || getDefaultHistoryVersion(versions);
	const typeBadge = getTypeBadge(props.doc.type);
	const targetBadge = getTargetBadge(props.doc.target);

	return (
		<DialogWrap
			open={props.open}
			onClose={props.onClose}
			disableAutoFocus
			disableRestoreFocus
			disableEnforceFocus
			title={`${props.doc.name} history`}
			actions={<UserFormServiceButtonClose onClick={props.onClose}>Close</UserFormServiceButtonClose>}
			login={true}>
			<DialogDocHeader>
				<DialogDocBadges>
					<Badge
						$color={typeBadge.color}
						$bg={typeBadge.bg}>
						{typeBadge.label}
					</Badge>
					<Badge
						$color={targetBadge.color}
						$bg={targetBadge.bg}>
						{targetBadge.label}
					</Badge>
				</DialogDocBadges>
			</DialogDocHeader>

			<DialogBody>
				{loading ? (
					<LoadingState>Loading version history...</LoadingState>
				) : (
					<HistoryLayout>
						<HistorySidebar>
							<HistorySidebarTitle>Saved versions</HistorySidebarTitle>
							<HistoryList>
								{versions.map((version, index) => (
									<HistoryItem
										key={version.id}
										type="button"
										$active={selectedVersion?.id === version.id}
										onClick={() => setSelectedVersionId(version.id)}>
										<HistoryItemTop>
											<HistoryVersion>Version {getVersionDisplayLabel(version)}</HistoryVersion>
											<div
												style={{
													display: "flex",
													gap: 6,
													flexWrap: "wrap",
													justifyContent: "flex-end",
												}}>
												{version.isActive && (
													<Badge
														$color="#2563eb"
														$bg="#dbeafe">
														Live
													</Badge>
												)}
												{version.requiresAcceptance && (
													<Badge
														$color="#92400e"
														$bg="#fef3c7">
														Requires acceptance
													</Badge>
												)}
												{!version.isPublished && (
													<Badge
														$color="#475569"
														$bg="#e2e8f0">
														Draft
													</Badge>
												)}
												{index === 0 && (
													<Badge
														$color="#2563eb"
														$bg="#dbeafe">
														Latest save
													</Badge>
												)}
											</div>
										</HistoryItemTop>
										<HistoryMeta>
											<div>
												{version.isPublished
													? formatDateTime(version.publishedAt)
													: "Not published"}
											</div>
											<div>{version.publishedByUserName || "System"}</div>
										</HistoryMeta>
									</HistoryItem>
								))}
								{versions.length === 0 && (
									<HistoryEmpty>
										No saved version yet. Current content is shown on the left for reference.
									</HistoryEmpty>
								)}
							</HistoryList>
						</HistorySidebar>

						<CompareSection>
							<CompareHeader>Compare previews</CompareHeader>
							<CompareGrid>
								<ComparePane>
									<ComparePaneHeader>
										<div>
											<ComparePaneTitle>Current content</ComparePaneTitle>
											<ComparePaneMeta>
												Last updated {formatDateTime(props.doc.updatedAt || null)}
											</ComparePaneMeta>
										</div>
									</ComparePaneHeader>
									<ComparePaneBody>
										{currentContent ? (
											<div dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentContent) }} />
										) : (
											<CompareEmpty>No current content.</CompareEmpty>
										)}
									</ComparePaneBody>
								</ComparePane>

								<ComparePane>
									<ComparePaneHeader>
										<div>
											<ComparePaneTitle>
												{selectedVersion
													? `Version ${getVersionDisplayLabel(selectedVersion)}`
													: "Selected version"}
											</ComparePaneTitle>
											<ComparePaneMeta>
												{selectedVersion
													? `${
															selectedVersion.isPublished
																? formatDateTime(selectedVersion.publishedAt)
																: "Not published"
													  } · ${selectedVersion.publishedByUserName || "System"}`
													: "Choose a saved version to compare"}
											</ComparePaneMeta>
										</div>
									</ComparePaneHeader>
									<ComparePaneBody>
										{selectedVersion?.content ? (
											<div
												dangerouslySetInnerHTML={{
													__html: sanitizeHtml(selectedVersion.content),
												}}
											/>
										) : (
											<CompareEmpty>No saved version selected.</CompareEmpty>
										)}
									</ComparePaneBody>
								</ComparePane>
							</CompareGrid>
						</CompareSection>
					</HistoryLayout>
				)}
			</DialogBody>
		</DialogWrap>
	);
}

/* ─── Doc Card ────────────────────────────────────────────────── */

function DocCard(props: {
	doc: StationDocument;
	onToggle: (id: number, next: boolean) => void;
	onEdit: (doc: StationDocument) => void;
	onHistory: (doc: StationDocument) => void;
}) {
	const { doc } = props;
	const typeBadge = getTypeBadge(doc.type);
	const targetBadge = getTargetBadge(doc.target);
	const contentLabels = extractContentLabels(doc.content);

	return (
		<Card>
			<CardTop>
				<FileIcon>
					<i className="fa fa-file-text-o" />
				</FileIcon>
				<CardInfo>
					<CardName>{doc.name}</CardName>
					<BadgeRow>
						<Badge
							$color={typeBadge.color}
							$bg={typeBadge.bg}>
							{typeBadge.label}
						</Badge>
						<Badge
							$color={targetBadge.color}
							$bg={targetBadge.bg}>
							{targetBadge.label}
						</Badge>
					</BadgeRow>
				</CardInfo>
			</CardTop>

			<ContentStatus $hasContent={!!doc.content}>
				<i className={doc.content ? "fa fa-check-circle" : "fa fa-circle-o"} />
				{doc.content ? `Has content · ${formatDate(doc.updatedAt)}` : "No content yet"}
			</ContentStatus>

			{contentLabels.length > 0 && (
				<ContentLabelsWrap>
					<ContentLabelsTitle>Content names</ContentLabelsTitle>
					<ContentLabelsList>
						{contentLabels.map((label) => (
							<ContentLabelChip key={label}>{label}</ContentLabelChip>
						))}
					</ContentLabelsList>
				</ContentLabelsWrap>
			)}

			<CardDivider />

			<CardFooter>
				<PermissionGate
					permission="documents.manage"
					fallback={
						<ToggleWrap>
							<ToggleTrack $on={doc.isActive} />
							<ToggleLabel $on={doc.isActive}>{doc.isActive ? "Active" : "Inactive"}</ToggleLabel>
						</ToggleWrap>
					}>
					<ToggleWrap>
						<ToggleInput
							type="checkbox"
							checked={doc.isActive}
							onChange={() => props.onToggle(doc.id, !doc.isActive)}
						/>
						<ToggleTrack $on={doc.isActive} />
						<ToggleLabel $on={doc.isActive}>{doc.isActive ? "Active" : "Inactive"}</ToggleLabel>
					</ToggleWrap>
				</PermissionGate>

				<ActionGroup>
					{doc.content && (
						<HistoryBtn
							type="button"
							onClick={() => props.onHistory(doc)}>
							<i className="fa fa-history" />
							History
						</HistoryBtn>
					)}
					<PermissionGate permission="documents.manage">
						<EditBtn
							type="button"
							onClick={() => props.onEdit(doc)}>
							<i className="fa fa-pencil" />
							{doc.content ? "Edit" : "Add content"}
						</EditBtn>
					</PermissionGate>
				</ActionGroup>
			</CardFooter>
		</Card>
	);
}


/* ─── Signing switch (super admin) ────────────────────────────── */
/* Quân chốt 06/10/2026: luồng chuẩn là BẮT KÝ; tắt chỉ để đăng văn bản lên prod cho trạm đọc thử /
   hoàn thiện pháp lý. Thay cho đường tắt cũ là đăng từng bản với "requires acceptance" = off. */

const SwitchCard = styled(Card)<{ $tone: "on" | "off" | "unset" }>`
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	border-color: ${({ $tone }) => ($tone === "on" ? "#bfdbfe" : $tone === "off" ? "#fde68a" : "#e5e7eb")};
	background: ${({ $tone }) => ($tone === "on" ? "#eff6ff" : $tone === "off" ? "#fffbeb" : "#ffffff")};
	margin-bottom: 24px;

	@media (max-width: 640px) {
		flex-direction: column;
		align-items: flex-start;
	}
`;

const SwitchText = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
`;

const SwitchTitle = styled.div`
	font-size: 15px;
	font-weight: 700;
	color: #111827;
`;

const SwitchDesc = styled.div`
	font-size: 13px;
	color: #4b5563;
	line-height: 1.5;
`;

const SwitchMeta = styled.div`
	font-size: 12px;
	color: #9ca3af;
`;

function SigningSwitchCard() {
	const [state, setState] = useState<StationSigningSwitch | null>(null);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		getStationSigningSwitch()
			.then(setState)
			.catch(() => setError("Could not load the signing switch."));
	}, []);

	if (!state && !error) return null;

	const on = state?.requireSigning === true;
	const tone: "on" | "off" | "unset" = state?.requireSigning === true ? "on" : state?.requireSigning === false ? "off" : "unset";
	const desc =
		tone === "on"
			? "On — every station must sign the current agreements before using the portal."
			: tone === "off"
				? "Off — active stations are not asked to sign. New stations still sign to go live."
				: "Not set — each document version decides (versions published without \"requires acceptance\" are not signed by active stations).";

	const handleChange = async () => {
		if (!state?.canEdit || saving) return;
		const next = !on;
		const warning = next
			? "Turn ON signing? Every active station will be asked to sign the current agreements the next time they open the portal."
			: "Turn OFF signing? Active stations will no longer be asked to sign. New stations still sign to go live.";
		if (!window.confirm(warning)) return;
		setSaving(true);
		setError("");
		try {
			setState(await setStationSigningSwitch(next));
		} catch (err: any) {
			setError(err?.response?.data?.message || "Could not save. Please try again.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<SwitchCard $tone={tone}>
			<SwitchText>
				<SwitchTitle>Require stations to sign</SwitchTitle>
				<SwitchDesc>{error || desc}</SwitchDesc>
				<SwitchMeta>
					{state?.updatedAt
						? `Last changed ${new Date(state.updatedAt).toLocaleString()}${state.updatedBy ? ` by ${state.updatedBy}` : ""}`
						: "Never changed"}
					{state && !state.canEdit ? " · Only a super admin can change this" : ""}
				</SwitchMeta>
			</SwitchText>
			<ToggleWrap style={{ cursor: state?.canEdit && !saving ? "pointer" : "not-allowed", opacity: state?.canEdit ? 1 : 0.6 }}>
				<ToggleInput
					type="checkbox"
					checked={on}
					disabled={!state?.canEdit || saving}
					onChange={handleChange}
					aria-label="Require stations to sign"
				/>
				<ToggleTrack $on={on} />
				<ToggleLabel $on={on}>{tone === "on" ? "On" : tone === "off" ? "Off" : "Not set"}</ToggleLabel>
			</ToggleWrap>
		</SwitchCard>
	);
}

/** 28/10/2026 (Quân, bước 3): giấy phép được duyệt ⇒ trạm ký lại hợp đồng có họ tên rồi mới rút tiền. Không khoá cổng trạm. */
function ResignSwitchCard() {
	const [state, setState] = useState<StationSigningSwitch | null>(null);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		getStationSigningSwitch()
			.then(setState)
			.catch(() => setError("Could not load the re-sign switch."));
	}, []);

	if (!state && !error) return null;

	const on = state?.resignAfterLicence === true;
	const desc = on
		? "On — once a station's business licence is approved, it must sign the agreements again (with the signer's name) before it can request a payout. The portal stays open."
		: "Off — approving a licence does not ask the station to sign again.";

	const handleChange = async () => {
		if (!state?.canEdit || saving) return;
		const next = !on;
		const warning = next
			? "Turn ON re-sign after licence approval? Stations with an approved licence will need to sign again before their next payout."
			: "Turn OFF re-sign after licence approval? Stations will no longer be asked to sign again before payouts.";
		if (!window.confirm(warning)) return;
		setSaving(true);
		setError("");
		try {
			setState(await setStationResignSwitch(next));
		} catch (err: any) {
			setError(err?.response?.data?.message || "Could not save. Please try again.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<SwitchCard $tone={on ? "on" : "off"}>
			<SwitchText>
				<SwitchTitle>Re-sign after licence approval</SwitchTitle>
				<SwitchDesc>{error || desc}</SwitchDesc>
				<SwitchMeta>{state && !state.canEdit ? "Super admin only" : "Applies to payouts only"}</SwitchMeta>
			</SwitchText>
			<ToggleWrap style={{ cursor: state?.canEdit && !saving ? "pointer" : "not-allowed", opacity: state?.canEdit ? 1 : 0.6 }}>
				<ToggleInput
					type="checkbox"
					checked={on}
					disabled={!state?.canEdit || saving}
					onChange={handleChange}
					aria-label="Re-sign after licence approval"
				/>
				<ToggleTrack $on={on} />
				<ToggleLabel $on={on}>{on ? "On" : "Off"}</ToggleLabel>
			</ToggleWrap>
		</SwitchCard>
	);
}

/* ─── Section (embeddable) ────────────────────────────────────── */

export function StationDocumentsSection() {
	const [docs, setDocs] = useState<StationDocument[]>([]);
	const [editorDoc, setEditorDoc] = useState<StationDocument | null>(null);
	const [historyDoc, setHistoryDoc] = useState<StationDocument | null>(null);

	const loadDocuments = useCallback(async () => {
		const nextDocs = await getStationDocuments();
		setDocs(nextDocs);
	}, []);

	useEffect(() => {
		loadDocuments();
	}, [loadDocuments]);

	const agreementDocs = docs.filter((d) => d.type === "agreement");
	const viewOnlyDocs = docs.filter((d) => d.type === "view_only");

	const handleToggle = async (id: number, next: boolean) => {
		await toggleStationDocument(id, next);
		await loadDocuments();
	};

	const handleSave = async (payload: {
		name: string;
		content: string;
		versionLabel: string;
		publishToStations: boolean;
	}) => {
		if (!editorDoc) return;
		await saveDocumentContent(editorDoc.id, payload);
		await loadDocuments();
	};

	return (
		<Inner>
			<PageHeader>
				<PageTitle>Station Documents</PageTitle>
				<PageMeta>Manage agreement documents and reference materials for stations.</PageMeta>
			</PageHeader>

			<SigningSwitchCard />
			<ResignSwitchCard />

			<Section>
				<SectionHeader>
					<SectionTitle>Requires acceptance</SectionTitle>
					<SectionLine />
					<SectionCount>
						{agreementDocs.length} document{agreementDocs.length !== 1 ? "s" : ""}
					</SectionCount>
				</SectionHeader>
				<Grid $cols={3}>
					{agreementDocs.map((doc) => (
						<DocCard
							key={doc.id}
							doc={doc}
							onToggle={handleToggle}
							onEdit={setEditorDoc}
							onHistory={setHistoryDoc}
						/>
					))}
				</Grid>
			</Section>

			<Section>
				<SectionHeader>
					<SectionTitle>Resources</SectionTitle>
					<SectionLine />
					<SectionCount>
						{viewOnlyDocs.length} document{viewOnlyDocs.length !== 1 ? "s" : ""}
					</SectionCount>
				</SectionHeader>
				<Grid $cols={2}>
					{viewOnlyDocs.map((doc) => (
						<DocCard
							key={doc.id}
							doc={doc}
							onToggle={handleToggle}
							onEdit={setEditorDoc}
							onHistory={setHistoryDoc}
						/>
					))}
				</Grid>
			</Section>

			<EditorDialog
				open={!!editorDoc}
				doc={editorDoc}
				onClose={() => setEditorDoc(null)}
				onSave={handleSave}
			/>
			<HistoryDialog
				open={!!historyDoc}
				doc={historyDoc}
				onClose={() => setHistoryDoc(null)}
			/>
		</Inner>
	);
}

/* ─── Standalone Page ─────────────────────────────────────────── */

export default function StationDocuments() {
	return (
		<Shell>
			<StationDocumentsSection />
		</Shell>
	);
}
